<template>
  <layout-header />
  <layout-sidebar />

  <div class="page-wrapper">
    <div class="content">
      <div class="row align-items-center mb-4">
        <div class="col-sm-6">
          <div class="d-flex align-items-center flex-wrap row-gap-3">
            <h6 class="fw-medium d-inline-flex align-items-center me-2">
              <router-link :to="{ name: 'crm.leads.list' }">
                <i class="ti ti-arrow-left me-2"></i>Leads
              </router-link>
              <span class="text-gray d-inline-flex ms-2">/ {{ lead?.name || "—" }}</span>
            </h6>

            <div v-if="lead?.pipeline?.name" class="dropdown">
              <a href="javascript:void(0);" class="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                <i class="ti ti-git-branch me-1"></i>{{ lead.pipeline.name }}
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li class="text-muted px-2 py-1">Pipeline</li>
                <li><a href="javascript:void(0);" class="dropdown-item rounded-1 disabled">{{ lead.pipeline.name }}</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div class="col-sm-6">
          <div class="d-flex justify-content-sm-end">
            <div class="head-icons ms-2">
              <a href="javascript:void(0);" id="collapse-header" @click="toggleHeader">
                <i class="ti ti-chevrons-up"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div v-if="loading && !lead" class="card border-0">
        <div class="card-body text-muted">Loading lead details...</div>
      </div>
      <div v-else-if="!lead" class="card border-0">
        <div class="card-body text-danger">Lead not found.</div>
      </div>

      <div v-else class="row">
        <div class="col-xl-4 theiaStickySidebar">
          <div class="card card-bg-1">
            <div class="card-body p-0">
              <span class="avatar avatar-xl border text-dark bg-white rounded-circle m-auto d-flex mb-2">
                {{ initials(lead.name) }}
              </span>

              <div class="text-center px-3 pb-3 border-bottom">
                <h5 class="d-flex align-items-center justify-content-center mb-1">{{ lead.name }}</h5>
                <p v-if="lead.company_name" class="text-dark mb-2">
                  <i class="ti ti-building me-1"></i>{{ lead.company_name }}
                </p>
                <div class="d-flex align-items-center justify-content-center gap-2 flex-wrap">
                  <span class="badge" :class="lead.visibility === 'restricted' ? 'badge-dark-transparent' : 'badge-success-transparent'">
                    <i class="ti" :class="lead.visibility === 'restricted' ? 'ti-lock me-1' : 'ti-world me-1'"></i>
                    {{ visibilityLabel }}
                  </span>
                  <span v-for="tag in lead.tags || []" :key="tag" class="badge" :class="tagClass(tag)">{{ tag }}</span>
                </div>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Lead Information</h6>
                <kv-row label="Date Created">{{ formatDate(lead.created_at) }}</kv-row>
                <kv-row label="Value">{{ money(lead.amount, lead.currency) }}</kv-row>
                <kv-row label="Source">{{ lead.source || "—" }}</kv-row>
                <kv-row label="Phone">{{ lead.phone || "—" }}</kv-row>
                <kv-row label="Email">{{ lead.email || "—" }}</kv-row>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Owner</h6>
                <div v-if="lead.owner" class="d-flex align-items-center">
                  <span class="avatar avatar-md avatar-rounded me-2">
                    <span class="d-inline-flex w-100 h-100 align-items-center justify-content-center bg-light text-dark">
                      {{ initials(ownerFullName) }}
                    </span>
                  </span>
                  <div>
                    <div class="fw-medium">{{ ownerFullName }}</div>
                    <small class="text-muted">{{ lead.owner.email || "No email" }}</small>
                  </div>
                </div>
                <div v-else class="text-muted">Unassigned</div>
              </div>

              <div class="p-3 border-bottom">
                <h6 class="mb-2">Collaborators</h6>
                <div v-if="lead.users?.length" class="d-flex flex-column gap-2">
                  <div v-for="user in lead.users" :key="user.id" class="d-flex align-items-center">
                    <span class="avatar avatar-md avatar-rounded me-2">
                      <span class="d-inline-flex w-100 h-100 align-items-center justify-content-center bg-light text-dark">
                        {{ initials(userFullName(user)) }}
                      </span>
                    </span>
                    <div>
                      <div class="fw-medium">{{ userFullName(user) }}</div>
                      <small class="text-muted">{{ user.email }}</small>
                    </div>
                  </div>
                </div>
                <div v-else class="text-muted">No collaborators.</div>
              </div>

              <div class="p-3">
                <h6 class="mb-2">Other Information</h6>
                <kv-row label="Stage">{{ lead.stage?.name || "—" }}</kv-row>
                <kv-row label="Industry">{{ lead.industry || "—" }}</kv-row>
                <kv-row label="Last Modified">{{ formatDate(lead.updated_at || lead.created_at) }}</kv-row>
              </div>
            </div>
          </div>
        </div>

        <div class="col-xl-8">
          <div class="bg-white rounded p-3 mb-3">
            <h4 class="fw-semibold mb-3">Lead Pipeline Status</h4>
            <div class="pipeline-list pipeline-list-two">
              <ul>
                <li><a href="javascript:void(0);" :class="['bg-purple', isTagActive('Not Contacted') ? 'opacity-100' : 'opacity-50']">Not Contacted</a></li>
                <li><a href="javascript:void(0);" :class="['bg-info', isTagActive('Contacted') ? 'opacity-100' : 'opacity-50']">Contacted</a></li>
                <li><a href="javascript:void(0);" :class="['bg-warning', isTagActive('Closed') ? 'opacity-100' : 'opacity-50']">Closed</a></li>
                <li><a href="javascript:void(0);" :class="['bg-danger', isTagActive('Lost') ? 'opacity-100' : 'opacity-50']">Lost</a></li>
              </ul>
            </div>
          </div>

          <crm-engagement-panel
            entity-type="lead"
            :entity-id="lead.id"
            :default-email="lead.email || ''"
            note-placeholder="Add an internal note about this lead"
          />
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a></p>
    </div>
  </div>
</template>

<script setup>
import { computed, defineComponent, h, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import CrmEngagementPanel from "@/components/crm/CrmEngagementPanel.vue";
import { useLeadStore } from "@/stores/leadStore";

const route = useRoute();
const router = useRouter();
const store = useLeadStore();

const id = computed(() => {
  const value = Number(route.params.id);
  return Number.isFinite(value) ? value : null;
});

const loading = computed(() => store.loading);
const lead = computed(() => (id.value ? store.byId?.[id.value] : null));
const ownerFullName = computed(() => {
  const owner = lead.value?.owner;
  return [owner?.first_name, owner?.last_name].filter(Boolean).join(" ") || owner?.email || "";
});
const visibilityLabel = computed(() => (lead.value?.visibility === "restricted" ? "Restricted" : "Public"));

async function loadLead(targetId) {
  if (!targetId) {
    router.replace({ name: "crm.leads.list" });
    return;
  }

  if (!store.byId?.[targetId]) {
    try {
      await store.fetchOne(targetId);
    } catch {}
  }
}

function toggleHeader() {
  document.getElementById("collapse-header")?.classList.toggle("active");
  document.body.classList.toggle("header-collapse");
}

function initials(value = "") {
  return String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "LD";
}

function userFullName(user) {
  return [user?.first_name, user?.middle_name, user?.last_name].filter(Boolean).join(" ") || user?.email || "Unnamed";
}

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
}

function money(amount, currency = "KES") {
  const value = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value);
  } catch {
    return `${currency} ${value.toLocaleString()}`;
  }
}

function tagClass(tag = "") {
  const normalized = tag.toLowerCase();
  if (normalized.includes("contacted")) return "badge-soft-info";
  if (normalized.includes("lost")) return "badge-soft-danger";
  if (normalized.includes("closed")) return "badge-soft-warning";
  return "badge-dark-transparent";
}

function isTagActive(tag) {
  return (lead.value?.tags || []).some((value) => String(value).toLowerCase() === String(tag).toLowerCase());
}

const KvRow = defineComponent({
  name: "KvRow",
  props: { label: { type: String, default: "" } },
  setup(props, { slots }) {
    return () =>
      h("div", { class: "d-flex align-items-center justify-content-between mb-2" }, [
        h("span", { class: "d-inline-flex align-items-center" }, [h("i", { class: "ti ti-dots me-2" }), props.label]),
        h("p", { class: "text-dark mb-0 text-end" }, slots.default ? slots.default() : "—"),
      ]);
  },
});

onMounted(async () => {
  await loadLead(id.value);
});

watch(id, async (value, previous) => {
  if (value && value !== previous) {
    await loadLead(value);
  }
});
</script>

<style scoped>
@media (max-width: 767.98px) {
  .row.align-items-center.mb-4 > [class*="col-"] {
    width: 100%;
  }

  .row.align-items-center.mb-4 .d-flex.justify-content-sm-end {
    justify-content: flex-start !important;
  }

  .card-body.p-0 {
    overflow: hidden;
  }
}
</style>


